import { Injectable } from '@angular/core';
import { Workbook, type Alignment, type Borders, type Fill, type Font, type Worksheet } from 'exceljs';
import { saveAs } from 'file-saver';
import { PDFDocument, rgb, StandardFonts, type PDFFont, type PDFPage, type RGB } from 'pdf-lib';
import {
  Granularity,
  type HistoryGranularity,
  type ProductionHistoryDashboardGridRow,
} from './production-history-dashboard.models';

export type ProductionHistoryExportColumn = {
  key: string;
  header: string;
  kind: 'text' | 'currency' | 'percent' | 'annual';
  year?: number;
  groupHeader?: string;
};

export type ProductionHistoryExportSection = {
  granularity: HistoryGranularity;
  title: string;
  columns: ProductionHistoryExportColumn[];
  rows: ProductionHistoryDashboardGridRow[];
  summaryRow?: ProductionHistoryDashboardGridRow | null;
  footnote?: string;
};

export type ProductionHistoryExportArgs = {
  fileName: string;
  /** "Production Tracking - <who>", as the page shows it. */
  title: string;
  /** "As of <date>", under the title; left out when there is no date. */
  periodLabel?: string | null;
  sections: ProductionHistoryExportSection[];
};

@Injectable()
export class ProductionHistoryExportService {
  private static readonly headerFillArgb = 'FF243D63';
  private static readonly borderArgb = 'FF111111';
  /** The gold border the top-producing cell carries, in Excel and in the PDF alike. */
  private static readonly topProductionGoldArgb = 'FFF6D87B';
  private static readonly monthlyRankFills: Record<number, string> = {
    1: 'FF76CC88',
    2: 'FFC5E7C7',
    3: 'FFC5E7C7',
  };
  private static readonly quarterlyTopFill = 'FFF1AF82';
  private static readonly projectionFill = 'FFD7EEF9';
  private static readonly pdfMargin = 24;
  private static readonly pdfLandscapePageSize: [number, number] = [792, 612];
  private static readonly pdfHeaderFontSize = 6;
  private static readonly pdfValueFontSize = 6.4;
  /** The floor a wide export (many year columns) may shrink the values to before they get cut. */
  private static readonly pdfMinValueFontSize = 4.6;
  private static readonly pdfLineHeightRatio = 1.2;
  private static readonly pdfGroupHeaderHeight = 14;

  async exportExcel(args: ProductionHistoryExportArgs): Promise<void> {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Production History', {
      properties: { defaultRowHeight: 20 },
      pageSetup: {
        orientation: 'portrait',
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 0,
        margins: {
          left: 0.25,
          right: 0.25,
          top: 0.35,
          bottom: 0.35,
          header: 0.1,
          footer: 0.1,
        },
      },
    });
    const maxColumns = Math.max(...args.sections.map(section => section.columns.length), 1);

    worksheet.views = [{ showGridLines: false }];
    worksheet.columns = Array.from({ length: maxColumns }, (_, index) => ({
      width: index === 0 ? 26 : 15,
    }));

    let rowCursor = this.writeExcelTitle(worksheet, args.title, args.periodLabel, maxColumns);

    for (const section of args.sections) {
      rowCursor = this.writeExcelSection(worksheet, section, rowCursor, maxColumns);
    }

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${args.fileName}.xlsx`);
  }

  async exportPdf(args: ProductionHistoryExportArgs): Promise<void> {
    const pdfDoc = await PDFDocument.create();
    const regularFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    const italicFont = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);
    let page = pdfDoc.addPage(ProductionHistoryExportService.pdfLandscapePageSize);
    let cursorY = page.getHeight() - ProductionHistoryExportService.pdfMargin;

    cursorY = this.drawPdfTitle({
      page,
      boldFont,
      italicFont,
      title: args.title,
      periodLabel: args.periodLabel,
      cursorY,
    });

    for (const section of args.sections) {
      const result = this.drawPdfSection({
        pdfDoc,
        page,
        cursorY,
        section,
        regularFont,
        boldFont,
        italicFont,
      });

      page = result.page;
      cursorY = result.cursorY;
    }

    const bytes = await pdfDoc.save();
    saveAs(new Blob([bytes], { type: 'application/pdf' }), `${args.fileName}.pdf`);
  }

  private writeExcelSection(
    worksheet: Worksheet,
    section: ProductionHistoryExportSection,
    startRow: number,
    maxColumns: number,
  ): number {
    const endColumn = Math.max(section.columns.length, 1);

    worksheet.mergeCells(startRow, 1, startRow, endColumn);
    const titleCell = worksheet.getCell(startRow, 1);
    titleCell.value = section.title;
    titleCell.font = this.excelTitleFont();
    titleCell.alignment = this.centerLeftAlignment();
    worksheet.getRow(startRow).height = 24;

    const hasGroupedHeaders = section.columns.some(column => column.groupHeader);
    const headerRow = startRow + 1;
    const dataStartRow = this.writeExcelHeaderRows(worksheet, section.columns, headerRow, hasGroupedHeaders);

    section.rows.forEach((row, rowIndex) => {
      const worksheetRow = dataStartRow + rowIndex;
      section.columns.forEach((column, columnIndex) => {
        const cell = worksheet.getCell(worksheetRow, columnIndex + 1);
        this.writeExcelDataCell(cell, row, column, section.granularity, false);
      });
    });

    let rowCursor = dataStartRow + section.rows.length;

    if (section.summaryRow) {
      section.columns.forEach((column, columnIndex) => {
        const cell = worksheet.getCell(rowCursor, columnIndex + 1);
        this.writeExcelDataCell(cell, section.summaryRow, column, section.granularity, true);
      });
      rowCursor += 1;
    }

    if (section.footnote) {
      worksheet.mergeCells(rowCursor, 1, rowCursor, maxColumns);
      const footnoteCell = worksheet.getCell(rowCursor, 1);
      footnoteCell.value = section.footnote;
      footnoteCell.font = {
        name: 'Arial',
        size: 10,
        bold: true,
        italic: true,
        color: { argb: 'FF111111' },
      };
      footnoteCell.alignment = this.centerLeftAlignment();
      rowCursor += 1;
    }

    return rowCursor + 2;
  }

  private writeExcelTitle(
    worksheet: Worksheet,
    title: string,
    periodLabel: string | null | undefined,
    maxColumns: number,
  ): number {
    worksheet.mergeCells(1, 1, 1, maxColumns);
    const titleCell = worksheet.getCell(1, 1);
    titleCell.value = title;
    titleCell.font = { name: 'Arial', size: 14, bold: true, color: { argb: 'FF1A3352' } };
    titleCell.alignment = this.centerLeftAlignment();
    worksheet.getRow(1).height = 26;

    if (!periodLabel) {
      return 3;
    }

    worksheet.mergeCells(2, 1, 2, maxColumns);
    const periodCell = worksheet.getCell(2, 1);
    periodCell.value = periodLabel;
    periodCell.font = { name: 'Arial', size: 10, italic: true, color: { argb: 'FF666666' } };
    periodCell.alignment = this.centerLeftAlignment();

    return 4;
  }

  /** The title, and the as-of line under it in the bulletins' muted italic. */
  private drawPdfTitle(args: {
    page: PDFPage;
    boldFont: PDFFont;
    italicFont: PDFFont;
    title: string;
    periodLabel?: string | null;
    cursorY: number;
  }): number {
    args.page.drawText(args.title, {
      x: ProductionHistoryExportService.pdfMargin,
      y: args.cursorY - 14,
      size: 14,
      font: args.boldFont,
      color: rgb(0.1, 0.2, 0.32),
    });

    if (!args.periodLabel) {
      return args.cursorY - 34;
    }

    args.page.drawText(args.periodLabel, {
      x: ProductionHistoryExportService.pdfMargin,
      y: args.cursorY - 28,
      size: 8,
      font: args.italicFont,
      color: rgb(0.4, 0.4, 0.4),
    });

    return args.cursorY - 44;
  }

  private drawPdfSection(args: {
    pdfDoc: PDFDocument;
    page: PDFPage;
    cursorY: number;
    section: ProductionHistoryExportSection;
    regularFont: PDFFont;
    boldFont: PDFFont;
    italicFont: PDFFont;
  }): { page: PDFPage; cursorY: number } {
    let page = args.page;
    let cursorY = args.cursorY;
    const allRows = args.section.summaryRow ? [...args.section.rows, args.section.summaryRow] : args.section.rows;
    const { columns } = args.section;
    const usableWidth = page.getWidth() - ProductionHistoryExportService.pdfMargin * 2;
    const columnWidths = this.getPdfColumnWidths(columns, usableWidth);
    const rowValues = allRows.map(row =>
      columns.map(column => this.formatValue(row[column.key], this.getResolvedColumnKind(row, column))),
    );
    const valueFontSize = this.getPdfValueFontSize(columns, columnWidths, rowValues, args.regularFont);
    const baseRowHeight = args.section.granularity === Granularity.Monthly ? 13 : 14;
    const rowHeights = rowValues.map(values =>
      this.getPdfRowHeight(columns, columnWidths, values, args.regularFont, valueFontSize, baseRowHeight),
    );
    const titleHeight = 15;
    const headerHeight = this.getPdfHeaderHeight(columns, columnWidths, args.boldFont);
    const footnoteHeight = args.section.footnote ? 14 : 0;
    const rowsHeight = rowHeights.reduce((sum, height) => sum + height, 0);
    const sectionHeight = titleHeight + headerHeight + rowsHeight + footnoteHeight + 14;

    if (cursorY - sectionHeight < ProductionHistoryExportService.pdfMargin) {
      page = args.pdfDoc.addPage(ProductionHistoryExportService.pdfLandscapePageSize);
      cursorY = page.getHeight() - ProductionHistoryExportService.pdfMargin;
    }

    const xStart = ProductionHistoryExportService.pdfMargin;

    page.drawText(args.section.title, {
      x: xStart,
      y: cursorY - 10,
      size: 10,
      font: args.boldFont,
      color: rgb(0.1, 0.2, 0.32),
    });
    cursorY -= titleHeight;

    this.drawPdfHeaderRows({
      page,
      y: cursorY,
      height: headerHeight,
      columns,
      columnWidths,
      color: rgb(1, 1, 1),
      boldFont: args.boldFont,
    });
    cursorY -= headerHeight;

    allRows.forEach((row, rowIndex) => {
      const isSummaryRow = row === args.section.summaryRow;
      this.drawPdfRow({
        page,
        y: cursorY,
        height: rowHeights[rowIndex],
        columns,
        values: rowValues[rowIndex],
        columnWidths,
        fontSize: valueFontSize,
        fill: isSummaryRow ? rgb(1, 1, 1) : undefined,
        color: rgb(0, 0, 0),
        font: isSummaryRow ? args.italicFont : args.regularFont,
        getCellFill: column => this.getPdfHighlightFill(row, column, args.section.granularity),
        isGoldBordered: column => this.isGlobalTopProductionCell(row, column, args.section.granularity),
      });
      cursorY -= rowHeights[rowIndex];
    });

    if (args.section.footnote) {
      page.drawText(args.section.footnote, {
        x: xStart,
        y: cursorY - 10,
        size: 8,
        font: args.italicFont,
        color: rgb(0, 0, 0),
      });
      cursorY -= footnoteHeight;
    }

    return { page, cursorY: cursorY - 12 };
  }

  private drawPdfRow(args: {
    page: PDFPage;
    y: number;
    height: number;
    columns: ProductionHistoryExportColumn[];
    values: string[];
    columnWidths: number[];
    fontSize: number;
    fill?: RGB;
    color: RGB;
    font: PDFFont;
    getCellFill?: (column: ProductionHistoryExportColumn) => RGB | undefined;
    isGoldBordered?: (column: ProductionHistoryExportColumn) => boolean;
  }): void {
    const size = args.fontSize;
    const lineHeight = size * ProductionHistoryExportService.pdfLineHeightRatio;
    let x = ProductionHistoryExportService.pdfMargin;

    args.columns.forEach((column, index) => {
      const width = args.columnWidths[index];
      const cellFill = args.getCellFill?.(column) ?? args.fill;

      if (cellFill) {
        args.page.drawRectangle({
          x,
          y: args.y - args.height,
          width,
          height: args.height,
          color: cellFill,
        });
      }

      args.page.drawRectangle({
        x,
        y: args.y - args.height,
        width,
        height: args.height,
        borderColor: rgb(0, 0, 0),
        borderWidth: 0.4,
      });

      if (args.isGoldBordered?.(column)) {
        const inset = 0.75;
        args.page.drawRectangle({
          x: x + inset,
          y: args.y - args.height + inset,
          width: width - inset * 2,
          height: args.height - inset * 2,
          borderColor: this.argbToRgb(ProductionHistoryExportService.topProductionGoldArgb),
          borderWidth: 1.5,
        });
      }

      const value = args.values[index] ?? '';
      const maxTextWidth = Math.max(width - 4, 4);
      const isText = column.kind === 'text';
      // Row titles wrap (the row grows to fit them); figures stay on one line.
      const lines = isText
        ? this.wrapText(value, args.font, size, maxTextWidth)
        : [this.truncateText(value, args.font, size, maxTextWidth)];
      const firstBaseline = args.y - args.height / 2 + ((lines.length - 1) * lineHeight) / 2 - size / 2 + 1;

      lines.forEach((line, lineIndex) => {
        const textWidth = args.font.widthOfTextAtSize(line, size);
        args.page.drawText(line, {
          x: isText ? x + 2 : Math.max(x + 2, x + width - textWidth - 2),
          y: firstBaseline - lineIndex * lineHeight,
          size,
          font: args.font,
          color: args.color,
        });
      });

      x += width;
    });
  }

  private writeExcelHeaderRows(
    worksheet: Worksheet,
    columns: ProductionHistoryExportColumn[],
    headerRow: number,
    hasGroupedHeaders: boolean,
  ): number {
    if (!hasGroupedHeaders) {
      columns.forEach((column, index) => {
        const cell = worksheet.getCell(headerRow, index + 1);
        cell.value = column.header;
        this.styleExcelHeaderCell(cell);
      });
      worksheet.getRow(headerRow).height = 48;

      return headerRow + 1;
    }

    const subHeaderRow = headerRow + 1;

    for (let columnIndex = 0; columnIndex < columns.length; columnIndex += 1) {
      const column = columns[columnIndex];
      const excelColumn = columnIndex + 1;

      if (!column.groupHeader) {
        worksheet.mergeCells(headerRow, excelColumn, subHeaderRow, excelColumn);
        const cell = worksheet.getCell(headerRow, excelColumn);
        cell.value = column.header;
        this.styleExcelHeaderCell(cell);
        this.styleExcelHeaderCell(worksheet.getCell(subHeaderRow, excelColumn));
        continue;
      }

      const groupStartIndex = columnIndex;
      let groupEndIndex = columnIndex;

      while (groupEndIndex + 1 < columns.length && columns[groupEndIndex + 1].groupHeader === column.groupHeader) {
        groupEndIndex += 1;
      }

      worksheet.mergeCells(headerRow, groupStartIndex + 1, headerRow, groupEndIndex + 1);
      const groupCell = worksheet.getCell(headerRow, groupStartIndex + 1);
      groupCell.value = column.groupHeader;
      this.styleExcelHeaderCell(groupCell);

      for (let groupedColumnIndex = groupStartIndex; groupedColumnIndex <= groupEndIndex; groupedColumnIndex += 1) {
        const subHeaderCell = worksheet.getCell(subHeaderRow, groupedColumnIndex + 1);
        subHeaderCell.value = columns[groupedColumnIndex].header;
        this.styleExcelHeaderCell(subHeaderCell);
      }

      columnIndex = groupEndIndex;
    }

    worksheet.getRow(headerRow).height = 22;
    worksheet.getRow(subHeaderRow).height = 42;

    return subHeaderRow + 1;
  }

  private drawPdfHeaderRows(args: {
    page: PDFPage;
    y: number;
    height: number;
    columns: ProductionHistoryExportColumn[];
    columnWidths: number[];
    color: RGB;
    boldFont: PDFFont;
  }): void {
    const hasGroupedHeaders = args.columns.some(column => column.groupHeader);

    const groupHeaderHeight = hasGroupedHeaders ? ProductionHistoryExportService.pdfGroupHeaderHeight : 0;
    const subHeaderHeight = args.height - groupHeaderHeight;
    const fill = rgb(0.14, 0.24, 0.39);
    let x = ProductionHistoryExportService.pdfMargin;

    for (let columnIndex = 0; columnIndex < args.columns.length; columnIndex += 1) {
      const column = args.columns[columnIndex];
      const width = args.columnWidths[columnIndex];

      if (!column.groupHeader) {
        this.drawPdfHeaderCell({
          page: args.page,
          x,
          y: args.y,
          width,
          height: args.height,
          text: column.header,
          font: args.boldFont,
          color: args.color,
          fill,
        });
        x += width;
        continue;
      }

      const groupStartIndex = columnIndex;
      let groupEndIndex = columnIndex;
      let groupWidth = width;

      while (
        groupEndIndex + 1 < args.columns.length &&
        args.columns[groupEndIndex + 1].groupHeader === column.groupHeader
      ) {
        groupEndIndex += 1;
        groupWidth += args.columnWidths[groupEndIndex];
      }

      this.drawPdfHeaderCell({
        page: args.page,
        x,
        y: args.y,
        width: groupWidth,
        height: groupHeaderHeight,
        text: column.groupHeader,
        font: args.boldFont,
        color: args.color,
        fill,
      });

      let subHeaderX = x;
      for (let groupedColumnIndex = groupStartIndex; groupedColumnIndex <= groupEndIndex; groupedColumnIndex += 1) {
        const subHeaderWidth = args.columnWidths[groupedColumnIndex];
        this.drawPdfHeaderCell({
          page: args.page,
          x: subHeaderX,
          y: args.y - groupHeaderHeight,
          width: subHeaderWidth,
          height: subHeaderHeight,
          text: args.columns[groupedColumnIndex].header,
          font: args.boldFont,
          color: args.color,
          fill,
        });
        subHeaderX += subHeaderWidth;
      }

      x += groupWidth;
      columnIndex = groupEndIndex;
    }
  }

  private drawPdfHeaderCell(args: {
    page: PDFPage;
    x: number;
    y: number;
    width: number;
    height: number;
    text: string;
    font: PDFFont;
    color: RGB;
    fill: RGB;
  }): void {
    args.page.drawRectangle({
      x: args.x,
      y: args.y - args.height,
      width: args.width,
      height: args.height,
      color: args.fill,
    });
    args.page.drawRectangle({
      x: args.x,
      y: args.y - args.height,
      width: args.width,
      height: args.height,
      borderColor: rgb(0, 0, 0),
      borderWidth: 0.4,
    });

    const size = ProductionHistoryExportService.pdfHeaderFontSize;
    const lineHeight = size * ProductionHistoryExportService.pdfLineHeightRatio;
    const lines = this.wrapText(args.text, args.font, size, Math.max(args.width - 3, 4));
    const firstBaseline = args.y - args.height / 2 + ((lines.length - 1) * lineHeight) / 2 - size / 2 + 1;

    lines.forEach((line, lineIndex) => {
      const textWidth = args.font.widthOfTextAtSize(line, size);
      args.page.drawText(line, {
        x: Math.max(args.x + 2, args.x + (args.width - textWidth) / 2),
        y: firstBaseline - lineIndex * lineHeight,
        size,
        font: args.font,
        color: args.color,
      });
    });
  }

  /** Tall enough for the most-wrapped header, and never shorter than the fixed header band. */
  private getPdfHeaderHeight(columns: ProductionHistoryExportColumn[], columnWidths: number[], font: PDFFont): number {
    const hasGroupedHeaders = columns.some(column => column.groupHeader);
    const size = ProductionHistoryExportService.pdfHeaderFontSize;
    const maxLines = Math.max(
      ...columns.map(
        (column, index) => this.wrapText(column.header, font, size, Math.max(columnWidths[index] - 3, 4)).length,
      ),
      1,
    );
    const groupHeaderHeight = hasGroupedHeaders ? ProductionHistoryExportService.pdfGroupHeaderHeight : 0;
    const neededHeight = groupHeaderHeight + maxLines * size * ProductionHistoryExportService.pdfLineHeightRatio + 8;

    return Math.max(hasGroupedHeaders ? 44 : 42, Math.ceil(neededHeight));
  }

  /**
   * One size for every figure in the section, shrunk just enough that the widest one fits its
   * column: with many year columns the full amounts used to be cut ("$11,387,392....").
   */
  private getPdfValueFontSize(
    columns: ProductionHistoryExportColumn[],
    columnWidths: number[],
    rowValues: string[][],
    font: PDFFont,
  ): number {
    const baseSize = ProductionHistoryExportService.pdfValueFontSize;
    let size = baseSize;

    rowValues.forEach(values => {
      columns.forEach((column, index) => {
        const text = values[index];

        if (column.kind === 'text' || !text) {
          return;
        }

        const available = Math.max(columnWidths[index] - 4, 4);
        const textWidth = font.widthOfTextAtSize(text, baseSize);

        if (textWidth > available) {
          size = Math.min(size, (baseSize * available) / textWidth);
        }
      });
    });

    return Math.max(ProductionHistoryExportService.pdfMinValueFontSize, Math.floor(size * 10) / 10);
  }

  private getPdfRowHeight(
    columns: ProductionHistoryExportColumn[],
    columnWidths: number[],
    values: string[],
    font: PDFFont,
    size: number,
    baseHeight: number,
  ): number {
    const maxLines = Math.max(
      ...columns.map((column, index) =>
        column.kind === 'text'
          ? this.wrapText(values[index] ?? '', font, size, Math.max(columnWidths[index] - 4, 4)).length
          : 1,
      ),
      1,
    );

    return Math.max(baseHeight, Math.ceil(maxLines * size * ProductionHistoryExportService.pdfLineHeightRatio + 5));
  }

  private writeExcelDataCell(
    cell: {
      value?: unknown;
      font?: Partial<Font>;
      alignment?: Partial<Alignment>;
      fill?: Fill;
      border?: Partial<Borders>;
      numFmt?: string;
    },
    row: ProductionHistoryDashboardGridRow,
    column: ProductionHistoryExportColumn,
    granularity: HistoryGranularity,
    isSummaryRow: boolean,
  ): void {
    const value = row[column.key];
    const resolvedKind = this.getResolvedColumnKind(row, column);
    const isTopProductionCell = this.isGlobalTopProductionCell(row, column, granularity);

    /**
     * Values only. The top month used to be marked with a star, which made the cell rich text:
     * Excel then read it as text and left it out of SUM and of the selection total, so the
     * year's biggest month went missing from every hand-checked sum. A gold border marks it
     * instead, here and in the PDF.
     */
    cell.value = value ?? null;
    cell.font = {
      name: 'Arial',
      size: isSummaryRow ? 10 : 11,
      bold: false,
      italic: isSummaryRow,
      color: { argb: 'FF000000' },
    };
    cell.alignment = {
      vertical: 'middle',
      horizontal: resolvedKind === 'text' ? 'left' : 'right',
      wrapText: true,
    };
    cell.fill = this.getExcelCellFill(row, column, granularity) ?? {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFFFFFFF' },
    };
    cell.border = isTopProductionCell
      ? this.gridBorder(ProductionHistoryExportService.topProductionGoldArgb, 'medium')
      : this.gridBorder(ProductionHistoryExportService.borderArgb);

    if (typeof value === 'number') {
      if (resolvedKind === 'currency') {
        cell.numFmt = '$#,##0.00;-$#,##0.00';
      }

      if (resolvedKind === 'percent') {
        cell.numFmt = '0.0%;-0.0%';
      }
    }
  }

  private styleExcelHeaderCell(cell: {
    font?: Partial<Font>;
    alignment?: Partial<Alignment>;
    fill?: Fill;
    border?: Partial<Borders>;
  }): void {
    cell.font = {
      name: 'Arial',
      size: 10,
      bold: true,
      italic: true,
      color: { argb: 'FFFFFFFF' },
    };
    cell.alignment = {
      vertical: 'bottom',
      horizontal: 'center',
      wrapText: true,
    };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: ProductionHistoryExportService.headerFillArgb },
    };
    cell.border = this.gridBorder(ProductionHistoryExportService.borderArgb);
  }

  private getExcelCellFill(
    row: ProductionHistoryDashboardGridRow,
    column: ProductionHistoryExportColumn,
    granularity: HistoryGranularity,
  ): Fill | undefined {
    const fill = this.getHighlightArgb(row, column, granularity);

    return fill
      ? {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: fill },
        }
      : undefined;
  }

  private getPdfHighlightFill(
    row: ProductionHistoryDashboardGridRow,
    column: ProductionHistoryExportColumn,
    granularity: HistoryGranularity,
  ): RGB | undefined {
    const fill = this.getHighlightArgb(row, column, granularity);

    if (!fill) {
      return undefined;
    }

    return this.argbToRgb(fill);
  }

  private getHighlightArgb(
    row: ProductionHistoryDashboardGridRow,
    column: ProductionHistoryExportColumn,
    granularity: HistoryGranularity,
  ): string | null {
    if (column.key === 'priorPeriodChange' && row.priorPeriodChangeIsProjectionDerived) {
      return ProductionHistoryExportService.projectionFill;
    }

    if (column.key === 'currentYearChange' && row.currentYearChangeIsProjectionDerived) {
      return ProductionHistoryExportService.projectionFill;
    }

    if (column.year && row.isProjectionHighlightedByYear?.[column.year]) {
      return ProductionHistoryExportService.projectionFill;
    }

    if (!column.year) {
      return null;
    }

    if (granularity === Granularity.Monthly) {
      const rank = row.monthlyRankByYear?.[column.year] ?? null;

      return rank ? (ProductionHistoryExportService.monthlyRankFills[rank] ?? null) : null;
    }

    if (granularity === Granularity.Quarterly) {
      if (row.quarterlyRankByYear?.[column.year] === 1) {
        return ProductionHistoryExportService.quarterlyTopFill;
      }
    }

    return null;
  }

  private isGlobalTopProductionCell(
    row: ProductionHistoryDashboardGridRow,
    column: ProductionHistoryExportColumn,
    granularity: HistoryGranularity,
  ): boolean {
    if (!column.year) {
      return false;
    }

    if (granularity === Granularity.Monthly) {
      return row.globalTopMonthByYear?.[column.year] === true;
    }

    if (granularity === Granularity.Quarterly) {
      return row.globalTopQuarterByYear?.[column.year] === true;
    }

    return false;
  }

  private getPdfColumnWidths(columns: ProductionHistoryExportColumn[], usableWidth: number): number[] {
    const labelWidth = Math.min(74, usableWidth * 0.16);
    const comparisonColumnCount = columns.filter(column => column.kind === 'percent' && !column.year).length;
    const comparisonWidth = comparisonColumnCount ? Math.min(50, usableWidth * 0.085) : 0;
    const fixedWidth = labelWidth + comparisonWidth * comparisonColumnCount;
    const yearColumnCount = columns.length - 1 - comparisonColumnCount;
    const yearWidth = yearColumnCount ? (usableWidth - fixedWidth) / yearColumnCount : 0;

    return columns.map(column => {
      if (column.kind === 'text') {
        return labelWidth;
      }

      if (column.kind === 'percent' && !column.year) {
        return comparisonWidth;
      }

      return yearWidth;
    });
  }

  private formatValue(value: unknown, kind: ProductionHistoryExportColumn['kind']): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (kind === 'currency' && typeof value === 'number') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    }

    if (kind === 'percent' && typeof value === 'number') {
      return new Intl.NumberFormat('en-US', {
        style: 'percent',
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(value);
    }

    return String(value);
  }

  private getResolvedColumnKind(
    row: ProductionHistoryDashboardGridRow,
    column: ProductionHistoryExportColumn,
  ): Exclude<ProductionHistoryExportColumn['kind'], 'annual'> {
    if (column.kind !== 'annual') {
      return column.kind;
    }

    return row.valueKind === 'percent' ? 'percent' : 'currency';
  }

  private truncateText(text: string, font: PDFFont, size: number, maxWidth: number): string {
    if (font.widthOfTextAtSize(text, size) <= maxWidth) {
      return text;
    }

    let truncated = text;
    while (truncated.length > 1 && font.widthOfTextAtSize(`${truncated}...`, size) > maxWidth) {
      truncated = truncated.slice(0, -1);
    }

    return `${truncated}...`;
  }

  /** Word-wraps to the width; a single word that is still too wide is cut like truncateText. */
  private wrapText(text: string, font: PDFFont, size: number, maxWidth: number): string[] {
    const lines: string[] = [];
    let current = '';

    for (const word of text.split(/\s+/).filter(Boolean)) {
      const candidate = current ? `${current} ${word}` : word;

      if (!current || font.widthOfTextAtSize(candidate, size) <= maxWidth) {
        current = candidate;
        continue;
      }

      lines.push(current);
      current = word;
    }

    lines.push(current);

    return lines.map(line => this.truncateText(line, font, size, maxWidth));
  }

  private argbToRgb(argb: string): RGB {
    const hex = argb.replace(/^FF/, '');
    const red = Number.parseInt(hex.slice(0, 2), 16) / 255;
    const green = Number.parseInt(hex.slice(2, 4), 16) / 255;
    const blue = Number.parseInt(hex.slice(4, 6), 16) / 255;

    return rgb(red, green, blue);
  }

  private excelTitleFont(): Partial<Font> {
    return {
      name: 'Arial',
      size: 12,
      bold: true,
      italic: true,
      color: { argb: 'FF111111' },
    };
  }

  private gridBorder(color: string, style: 'thin' | 'medium' = 'thin'): Partial<Borders> {
    return {
      top: { style, color: { argb: color } },
      left: { style, color: { argb: color } },
      bottom: { style, color: { argb: color } },
      right: { style, color: { argb: color } },
    };
  }

  private centerLeftAlignment(): Partial<Alignment> {
    return {
      vertical: 'middle',
      horizontal: 'left',
    };
  }
}
